import React from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Plus, Package, Truck, ClipboardCheck, FileText, Building2, 
  LogOut, Home, Navigation
} from 'lucide-react';

// Sidebar Menu Item
const MenuItem = ({ icon: Icon, label, active, onClick, badge }) => (
  <button
    onClick={onClick}
    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
      active 
        ? 'bg-[#36e961] text-[#0a3042] font-semibold' 
        : 'text-gray-300 hover:bg-white/10 hover:text-white'
    }`}
    data-testid={`sidebar-${label.toLowerCase().replace(' ', '-')}`}
  >
    <Icon className="w-5 h-5" />
    <span className="text-sm">{label}</span>
    {badge && (
      <span className="ml-auto bg-[#36e961] text-[#0a3042] text-xs font-bold px-2 py-0.5 rounded-full">
        {badge}
      </span>
    )}
  </button>
);

const ClientSidebar = ({ user, activeTab, onNavigate, onCreateService, onLogout, stats = {} }) => {
  const { t } = useTranslation();
  return (
    <aside className="w-64 bg-[#0a3042] flex flex-col shrink-0">
      {/* Logo */}
      <div className="p-6 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center p-1.5">
            <img 
              src="/brand/autogo-logo.svg" 
              alt={t('app_logo_alt')} 
              className="w-full h-full object-contain"
            />
          </div>
          <div>
            <h1 className="text-white font-bold text-lg">AutoGo</h1>
            <p className="text-gray-400 text-xs">{t('client_sidebar_subtitle')}</p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        <p className="text-gray-500 text-xs uppercase tracking-wider px-4 mb-2">{t('client_sidebar_section_general')}</p>
        <MenuItem 
          icon={Home} 
          label={t('client_sidebar_dashboard')} 
          active={activeTab === 'dashboard'}
          onClick={() => onNavigate('dashboard')}
        />
        <MenuItem 
          icon={Package} 
          label={t('my_orders')} 
          active={activeTab === 'services'}
          onClick={() => onNavigate('services')}
          badge={stats.total > 0 ? stats.total : null}
        />
        <MenuItem 
          icon={Plus} 
          label={t('create_order')} 
          active={activeTab === 'create'}
          onClick={() => {
            onNavigate('create');
            onCreateService?.();
          }}
        />

        <p className="text-gray-500 text-xs uppercase tracking-wider px-4 mb-2 mt-6">{t('client_sidebar_section_tracking')}</p>
        <MenuItem 
          icon={Navigation} 
          label={t('gps_live_title')} 
          active={activeTab === 'gps'}
          onClick={() => onNavigate('gps')}
          badge={stats.in_transit > 0 ? stats.in_transit : null}
        />
        <MenuItem 
          icon={Truck} 
          label={t('client_home_service_status_menu')} 
          active={activeTab === 'deliveries'}
          onClick={() => onNavigate('deliveries')}
          badge={stats.in_progress > 0 ? stats.in_progress : null}
        />
        <MenuItem 
          icon={ClipboardCheck} 
          label={t('client_home_inspections_menu')} 
          active={activeTab === 'inspections'}
          onClick={() => onNavigate('inspections')}
        />

        <p className="text-gray-500 text-xs uppercase tracking-wider px-4 mb-2 mt-6">{t('client_sidebar_section_account')}</p>
        <MenuItem 
          icon={FileText} 
          label={t('client_home_invoices_menu')} 
          active={activeTab === 'invoices'}
          onClick={() => onNavigate('invoices')}
        />
        <MenuItem 
          icon={Building2} 
          label={t('my_company')} 
          active={activeTab === 'profile'}
          onClick={() => onNavigate('profile')}
        />
      </nav>

      {/* User & Logout */}
      <div className="p-4 border-t border-white/10">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 bg-gray-600 rounded-full flex items-center justify-center text-white font-bold">
            {user?.company_name?.[0] || user?.name?.[0] || 'C'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white text-sm font-medium truncate">{user?.company_name || user?.name}</p>
            <p className="text-gray-400 text-xs truncate">{user?.email}</p>
          </div>
        </div>
        <button
          onClick={onLogout}
          className="w-full flex items-center gap-2 px-4 py-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-all text-sm"
          data-testid="sidebar-logout"
        >
          <LogOut className="w-4 h-4" />
          {t('logout')}
        </button>
      </div>
    </aside>
  );
};

export default ClientSidebar;
